import {
    createProductAd,
    getProductAds,
} from 'api/Cerebro/orgScoped/amazonAdsApi'
import {
    CerebroPaginatedResponse,
    MakeCerebroApiRequestFunc,
} from 'api/Cerebro/types'
import { ENABLED } from 'const/resourceStates'
import { ProductAd } from 'types'

import {
    PRODUCT_AD_CREATE_BATCH_SIZE,
    PRODUCT_AD_LOOKUP_CHUNK_SIZE,
    PRODUCT_AD_LOOKUP_PAGE_SIZE,
    ProductAdSubmitCounts,
    classifyIncomingProductAds,
    existingProductAdIdentifier,
    extractCerebroErrorMessage,
    incomingProductAdIdentifier,
    isDuplicateProductAdError,
    runInBatches,
} from './classifyProductAds'
import { ProductAdField } from './localTypes'

type SubmitKind = 'created' | 'enabled' | 'alreadyEnabled' | 'archived'

interface SubmitJob {
    product: ProductAdField
    kind: SubmitKind
}

const emptyCounts = (): ProductAdSubmitCounts => ({
    created: 0,
    enabled: 0,
    alreadyEnabled: 0,
    archived: 0,
    alreadyInAdGroup: 0,
    failed: 0,
})

function fieldFromExistingAd(
    productAd: ProductAd,
    isSeller: boolean
): ProductAdField | null {
    const identifier = existingProductAdIdentifier(productAd, isSeller)
    if (!identifier) {
        return null
    }
    return {
        asin: identifier,
        adGroupId: productAd.ad_group_id,
        state: ENABLED,
    }
}

async function fetchExistingChunk(
    adGroupId: string,
    identifiers: string[],
    isSeller: boolean,
    makeCerebroApiRequest: MakeCerebroApiRequestFunc
): Promise<ProductAd[]> {
    const identifierSet = new Set(identifiers)
    const matched: ProductAd[] = []
    const matchedIdentifiers = new Set<string>()
    let offset = 0

    while (true) {
        const response = await makeCerebroApiRequest<
            ProductAd,
            CerebroPaginatedResponse<ProductAd>
        >({
            request: getProductAds({
                ad_group: adGroupId,
                limit: PRODUCT_AD_LOOKUP_PAGE_SIZE,
                offset,
                ...(isSeller
                    ? { sku__in: identifiers.join(',') }
                    : { asin__in: identifiers.join(',') }),
            }),
            suppressErrorAlerts: true,
        })

        if (!response) {
            break
        }

        const page = response.data.results ?? []
        page.forEach((productAd) => {
            const identifier = existingProductAdIdentifier(productAd, isSeller)
            if (
                identifier &&
                identifierSet.has(identifier) &&
                productAd.ad_group_id === adGroupId
            ) {
                matched.push(productAd)
                matchedIdentifiers.add(identifier)
            }
        })

        offset += page.length
        const total = response.data.count ?? offset
        const exhaustedResults = page.length === 0 || offset >= total
        const foundAllRequested = matchedIdentifiers.size >= identifiers.length
        if (exhaustedResults || foundAllRequested) {
            break
        }
    }

    return matched
}

async function fetchExistingProductAds(
    products: ProductAdField[],
    isSeller: boolean,
    makeCerebroApiRequest: MakeCerebroApiRequestFunc
): Promise<ProductAd[]> {
    const identifiersByAdGroup = new Map<string, string[]>()
    products.forEach((product) => {
        const identifier = incomingProductAdIdentifier(product)
        if (!identifier || !product.adGroupId) {
            return
        }
        const identifiers = identifiersByAdGroup.get(product.adGroupId) ?? []
        if (!identifiers.includes(identifier)) {
            identifiers.push(identifier)
        }
        identifiersByAdGroup.set(product.adGroupId, identifiers)
    })

    const existing: ProductAd[] = []
    for (const [adGroupId, identifiers] of identifiersByAdGroup) {
        for (
            let index = 0;
            index < identifiers.length;
            index += PRODUCT_AD_LOOKUP_CHUNK_SIZE
        ) {
            const chunk = identifiers.slice(
                index,
                index + PRODUCT_AD_LOOKUP_CHUNK_SIZE
            )
            existing.push(
                ...(await fetchExistingChunk(
                    adGroupId,
                    chunk,
                    isSeller,
                    makeCerebroApiRequest
                ))
            )
        }
    }
    return existing
}

function buildSubmitJobs(
    products: ProductAdField[],
    existing: ProductAd[],
    isSeller: boolean
): SubmitJob[] {
    const classified = classifyIncomingProductAds(products, existing, isSeller)
    const jobs: SubmitJob[] = [
        ...classified.toCreate.map((product) => ({
            product,
            kind: 'created' as const,
        })),
        ...classified.alreadyEnabled.map((product) => ({
            product,
            kind: 'alreadyEnabled' as const,
        })),
    ]

    classified.paused.forEach((productAd) => {
        const product = fieldFromExistingAd(productAd, isSeller)
        if (product) {
            jobs.push({ product, kind: 'enabled' })
        }
    })
    classified.archived.forEach((productAd) => {
        const product = fieldFromExistingAd(productAd, isSeller)
        if (product) {
            jobs.push({ product, kind: 'archived' })
        }
    })

    return jobs
}

export async function submitProductAds({
    products,
    campaignId,
    isSeller,
    makeCerebroApiRequest,
}: {
    products: ProductAdField[]
    campaignId: string | undefined
    isSeller: boolean
    makeCerebroApiRequest: MakeCerebroApiRequestFunc
}): Promise<ProductAdSubmitCounts> {
    const counts = emptyCounts()
    const existing = await fetchExistingProductAds(
        products,
        isSeller,
        makeCerebroApiRequest
    )
    const jobs = buildSubmitJobs(products, existing, isSeller)

    const results = await runInBatches(
        jobs,
        PRODUCT_AD_CREATE_BATCH_SIZE,
        async (job) => {
            let failureMessage: string | null = null
            const response = await makeCerebroApiRequest<ProductAd>({
                request: createProductAd({
                    ...(isSeller
                        ? { sku: job.product.asin }
                        : { asin: job.product.asin }),
                    ad_group_id: job.product.adGroupId,
                    state: job.product.state,
                    campaign_id: campaignId,
                }),
                suppressErrorAlerts: true,
                onRequestFailure: (failedResponse) => {
                    failureMessage = extractCerebroErrorMessage(
                        failedResponse?.data
                    )
                },
            })
            return { job, created: response !== null, failureMessage }
        }
    )

    results.forEach(({ job, created, failureMessage }) => {
        if (created) {
            counts[job.kind] += 1
            return
        }
        if (failureMessage && isDuplicateProductAdError(failureMessage)) {
            counts.alreadyInAdGroup += 1
            return
        }
        counts.failed += 1
    })

    return counts
}
